<template>
  <!-- The carton account: the invoice PDF's three money blocks, live (locked
       answer L-B). Every number is the backend's `cartonAccount` — this
       component orders and words them, it never does money math. Money-line
       nouns are the PDF's; the one sentence (the top-up ask) is the club's
       voice (L-E). -->
  <f7-card class="carton-account elevation-4">
    <f7-card-content class="ca-header">
      <div class="ca-name">{{ cartonName }}</div>
      <div v-if="shippedLine" class="ca-meta">{{ shippedLine }}</div>
      <div class="ca-state-row">
        <span class="ca-chip" :class="`ca-chip-${state}`">{{ stateLabel }}</span>
        <span v-if="confirmedLine" class="ca-meta">{{ confirmedLine }}</span>
      </div>
    </f7-card-content>

    <!-- 0 · بيانات العضو والتوصيل — first block, as on the PDF: who the carton went
         to, the phone and address the courier used, and the delivery date. -->
    <section v-if="memberRows.length" class="ca-block" data-block="member">
      <div class="ca-block-heading">{{ $t('box.totals.account.member.heading') }}</div>
      <div class="ca-who">
        <div
          v-for="row in memberRows"
          :key="row.key"
          class="ca-who-item"
          :data-row="`member-${row.key}`"
        >
          <span class="ca-who-label">{{ row.label }}</span>
          <span class="ca-who-value" :class="{ 'ca-ltr': row.ltr }">{{ row.value }}</span>
        </div>
      </div>
    </section>

    <!-- 1 · الحساب -->
    <section class="ca-block" data-block="bill">
      <div class="ca-block-heading">{{ $t('box.totals.account.bill.heading') }}</div>
      <div class="ca-row">
        <span>{{ $t('box.totals.account.bill.items', { count: header.lineCount ?? 0 }) }}</span>
        <span class="ca-num">{{ formatCurrency(bill.itemsCents) }}</span>
      </div>
      <div class="ca-row">
        <span>{{ $t('box.totals.account.bill.clubSupport', { rate: clubSupportPct }) }}</span>
        <span class="ca-num">{{ formatCurrency(bill.clubSupportCents) }}</span>
      </div>
      <div
        v-for="(adjustment, index) in adjustments"
        :key="`adj-${index}`"
        class="ca-row ca-row-adjustment"
      >
        <span class="ca-label-wrap">{{ adjustment.label }}</span>
        <span class="ca-num" :class="{ 'ca-pos': adjustment.amountCents < 0 }">
          {{ formatSignedCurrency(adjustment.amountCents) }}
        </span>
      </div>
      <div class="ca-row ca-row-total">
        <span>{{ $t('box.totals.account.bill.total') }}</span>
        <span class="ca-num">{{ formatCurrency(bill.totalCents) }}</span>
      </div>
    </section>

    <!-- 2 · السداد, read as an equation a member can check with a pencil:
         before, minus what was charged, equals the highlighted balance after.
         A zero-total carton places no charge row, so there is nothing to
         settle and the block stays out. -->
    <section v-if="settlement.walletChargeCents != null" class="ca-block" data-block="settlement">
      <div class="ca-block-heading">{{ $t('box.totals.account.settlement.heading') }}</div>
      <div v-if="settlement.balanceBeforeCents != null" class="ca-row">
        <span>{{ $t('box.totals.account.settlement.balanceBefore') }}</span>
        <span class="ca-num">{{ formatCurrency(settlement.balanceBeforeCents) }}</span>
      </div>
      <div class="ca-row">
        <span>{{ walletChargeLabel }}</span>
        <span class="ca-num">{{ formatSignedCurrency(-settlement.walletChargeCents) }}</span>
      </div>

      <template v-if="cashAtShops.length">
        <div class="ca-row ca-row-sub">
          <span class="ca-label-wrap">{{ $t('box.totals.account.settlement.cashAtShops') }}</span>
          <span class="ca-num">{{ formatCurrency(settlement.cashAtShopsCents) }}</span>
        </div>
        <button
          v-for="cash in cashAtShops"
          :key="cash.transactionId"
          type="button"
          class="ca-row ca-row-sub ca-row-indent ca-row-tap"
          data-row="cash-at-shop"
          @click="emit('open-transaction', cash.transactionId)"
        >
          <span class="ca-label-wrap">{{ cashLine(cash) }}</span>
          <span class="ca-num">{{ formatCurrency(cash.amountCents) }}</span>
        </button>
      </template>

      <div
        v-if="settlement.balanceAfterCents != null"
        class="ca-hero"
        :class="{ 'ca-hero-neg': settlement.balanceAfterCents < 0 }"
        data-row="balance-after"
      >
        <div class="ca-hero-label">{{ balanceAfterLabel }}</div>
        <div class="ca-hero-value">{{ formatCurrency(settlement.balanceAfterCents) }}</div>
        <div class="ca-hero-meaning">{{ balanceAfterMeaning }}</div>
      </div>

      <div v-if="settlement.heldForEarlierCents > 0" class="ca-row ca-row-sub" data-row="held">
        <span class="ca-label-wrap">{{ $t('box.totals.account.settlement.heldForEarlier') }}</span>
        <span class="ca-num">{{ formatCurrency(settlement.heldForEarlierCents) }}</span>
      </div>
      <div v-if="settlement.availableAfterCents != null" class="ca-row ca-row-sub" data-row="available">
        <span class="ca-label-wrap">{{ $t('box.totals.account.settlement.availableAfter') }}</span>
        <span class="ca-num">{{ formatCurrency(settlement.availableAfterCents) }}</span>
      </div>

      <!-- «المستحق» + the ask (L-B): the wallet was left short, so say what
           to top up — no red pill on a carton he already received. -->
      <template v-if="isDue">
        <div class="ca-row ca-row-due" data-row="due">
          <span>{{ $t('box.totals.account.settlement.due') }}</span>
          <span class="ca-num ca-neg">{{ formatCurrency(header.dueCents) }}</span>
        </div>
        <div class="ca-ask" data-row="top-up-ask">
          {{ $t('box.totals.account.settlement.topUpAsk', { amount: formatCurrency(header.dueCents) }) }}
          <!-- One tap from the ask to the wallet, where the top-up sheet lives. -->
          <f7-button
            small
            fill
            round
            color="orange"
            class="ca-ask-cta"
            @click="emit('top-up')"
          >
            {{ $t('box.totals.account.settlement.topUpCta') }}
          </f7-button>
        </div>
      </template>
    </section>

    <!-- 3 · الصافي — only once the charge is confirmed; while it is a hold
         nothing has settled yet to net against. -->
    <section v-if="showNet" class="ca-block" data-block="net">
      <div class="ca-block-heading">{{ $t('box.totals.account.net.heading') }}</div>
      <div class="ca-row">
        <span class="ca-label-wrap">{{ $t('box.totals.account.net.after') }}</span>
        <span class="ca-num" :class="{ 'ca-pos': after.afterCents > 0 }">
          {{ formatSignedCurrency(after.afterCents) }}
        </span>
      </div>
      <button
        v-for="row in afterRows"
        :key="row.transactionId"
        type="button"
        class="ca-row ca-row-sub ca-row-indent ca-row-tap"
        data-row="after"
        @click="emit('open-transaction', row.transactionId)"
      >
        <span class="ca-label-wrap">{{ afterLine(row) }}</span>
        <span class="ca-num" :class="{ 'ca-pos': row.amountCents > 0 }">
          {{ formatSignedCurrency(row.amountCents) }}
        </span>
      </button>
      <div class="ca-row ca-row-total">
        <span>{{ $t('box.totals.account.net.netCost') }}</span>
        <span class="ca-num ca-big">{{ formatCurrency(after.netCostCents) }}</span>
      </div>
    </section>

    <!-- 4 · مكاسبك من هذه الكرتونة — last of the money blocks, kept out of the net
         cost: PoolShare per program and PoolCredit per shop. «متوقعة» until the
         receipt is confirmed. Only when something was earned or is expected. -->
    <section v-if="showGains" class="ca-block ca-block-gains" data-block="gains">
      <div class="ca-block-heading">{{ gainsHeading }}</div>
      <div class="ca-meta ca-gains-state">{{ gainsStateLine }}</div>

      <template v-if="poolShare.totalCents > 0">
        <div class="ca-row" data-row="poolshare">
          <span class="ca-label-wrap">{{ poolShareLabel }}</span>
          <span class="ca-num ca-pos">{{ formatCurrency(poolShare.totalCents) }}</span>
        </div>
        <div
          v-for="(program, index) in poolShare.programs"
          :key="`ps-${index}`"
          class="ca-row ca-row-sub ca-row-indent"
          data-row="poolshare-program"
        >
          <span class="ca-label-wrap">
            {{ programLine(program) }}
            <span v-if="program.isGift" class="ca-gift">{{ $t('box.totals.account.gains.giftTag') }}</span>
          </span>
          <span class="ca-num">{{ formatCurrency(program.cents) }}</span>
        </div>
        <div v-if="poolShare.balanceCents != null" class="ca-row ca-row-sub ca-row-indent" data-row="poolshare-balance">
          <span>{{ $t('box.totals.account.gains.poolShareBalance', { amount: formatCurrency(poolShare.balanceCents) }) }}</span>
        </div>
      </template>

      <template v-if="poolCredit.totalCents > 0">
        <div class="ca-row" data-row="poolcredit">
          <span class="ca-label-wrap">{{ poolCreditLabel }}</span>
          <span class="ca-num ca-pos">{{ formatCurrency(poolCredit.totalCents) }}</span>
        </div>
        <component
          :is="row.transactionId ? 'button' : 'div'"
          v-for="(row, index) in poolCredit.rows"
          :key="`pc-${index}`"
          :type="row.transactionId ? 'button' : undefined"
          class="ca-row ca-row-sub ca-row-indent"
          :class="{ 'ca-row-tap': row.transactionId }"
          data-row="poolcredit-shop"
          @click="row.transactionId && emit('open-transaction', row.transactionId)"
        >
          <span class="ca-label-wrap">{{ poolCreditLine(row) }}</span>
          <span class="ca-num">{{ formatCurrency(row.cents) }}</span>
        </component>
      </template>
    </section>

    <carton-items-by-shop :account="account" :box-items="boxItems"></carton-items-by-shop>

    <carton-mini-statement
      :account="account"
      @open-transaction="(id) => emit('open-transaction', id)"
    ></carton-mini-statement>

    <!-- The same account as a file he can keep or forward. -->
    <f7-card-content class="ca-pdf">
      <f7-button
        outline
        large
        class="ca-pdf-cta"
        :disabled="downloading"
        @click="emit('download-pdf')"
      >
        <f7-preloader v-if="downloading" size="18" class="ca-pdf-icon"></f7-preloader>
        <f7-icon
          v-else
          f7="doc_text_fill"
          size="18"
          class="ca-pdf-icon"
        ></f7-icon>
        {{ $t('box.totals.account.downloadPdf') }}
      </f7-button>
    </f7-card-content>

    <!-- Small print (L-A): the invoice number lives here, never at the top. -->
    <f7-card-content v-if="footerLine" class="ca-footer">
      {{ footerLine }}
    </f7-card-content>
  </f7-card>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CartonItemsByShop from '@/components/orders/CartonItemsByShop.vue';
import CartonMiniStatement from '@/components/orders/CartonMiniStatement.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import {
  basisPointsToPct,
  CARTON_STATE,
  cartonRowTitle,
  formatCartonDate,
  programArabicName,
} from '@/utils/cartonAccount.js';

const props = defineProps({
  // The order box's `cartonAccount` (never null here — the page falls back to
  // the old breakdown when it is).
  account: {
    type: Object,
    required: true,
  },
  // The box's OrderBoxItem records — only for the per-line issue chips.
  boxItems: {
    type: Array,
    default: () => [],
  },
  // True while the page fetches the PDF's download URL.
  downloading: {
    type: Boolean,
    default: false,
  },
  // Fallback name when the header carries no title.
  fallbackName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['top-up', 'open-transaction', 'download-pdf']);

const { t, te, locale } = useI18n();
const { formatCurrency, formatSignedCurrency } = useCurrency();

const header = computed(() => props.account?.header ?? {});
const bill = computed(() => props.account?.bill ?? {});
const settlement = computed(() => props.account?.settlement ?? {});
const after = computed(() => props.account?.after ?? {});

const state = computed(() => header.value.state ?? CARTON_STATE.PENDING);
const isDue = computed(() => state.value === CARTON_STATE.DUE && (header.value.dueCents ?? 0) > 0);
const isProjected = computed(() => Boolean(settlement.value.isProjected));
const showNet = computed(() => !isProjected.value && state.value !== CARTON_STATE.PENDING);

const cartonName = computed(() => header.value.title || props.fallbackName);

const date = (iso, opts) => formatCartonDate(iso, locale.value, opts);

const shippedLine = computed(() => {
  const shipped = date(header.value.shippedAt, { weekday: true });
  if (!shipped) return '';
  return t('box.totals.account.shippedLine', { date: shipped, count: header.value.lineCount ?? 0 });
});

const confirmedLine = computed(() => {
  const confirmed = date(header.value.deliveredConfirmedAt);
  return confirmed ? t('box.totals.account.confirmedOn', { date: confirmed }) : '';
});

const stateLabel = computed(() => {
  if (isDue.value) {
    return t('box.totals.account.state.due', { amount: formatCurrency(header.value.dueCents) });
  }
  if (state.value === CARTON_STATE.PAID) return t('box.totals.account.state.paid');
  return t('box.totals.account.state.pending');
});

// Member and delivery details (PDF's first block). A row with no value is left out,
// and the whole block with it when nothing is known.
const memberRows = computed(() => {
  const h = header.value;
  const rows = [];
  const name = [h.memberName, h.memberCode].filter(Boolean).join(' · ');
  if (name) rows.push({ key: 'name', label: t('box.totals.account.member.name'), value: name });
  if (h.shipPhone) rows.push({ key: 'phone', label: t('box.totals.account.member.phone'), value: h.shipPhone, ltr: true });
  const address = [h.shipAddress, h.shipGovernorate].filter(Boolean).join(' · ');
  if (address) rows.push({ key: 'address', label: t('box.totals.account.member.address'), value: address });
  const delivered = date(h.deliveredAt);
  if (delivered) rows.push({ key: 'delivery', label: t('box.totals.account.member.delivery'), value: delivered });
  return rows;
});

const clubSupportPct = computed(() => basisPointsToPct(bill.value.clubSupportRateBp));
const adjustments = computed(() => (Array.isArray(bill.value.adjustments) ? bill.value.adjustments : []));

const walletChargeLabel = computed(() => (isProjected.value
  ? t('box.totals.account.settlement.walletChargeProjected')
  : t('box.totals.account.settlement.walletCharge')));

// The date the wallet balance is «after this invoice» on: the receipt confirmation.
const balanceAfterLabel = computed(() => {
  if (isProjected.value) return t('box.totals.account.settlement.balanceAfterProjected');
  const on = date(header.value.deliveredConfirmedAt);
  return on
    ? t('box.totals.account.settlement.balanceAfterOn', { date: on })
    : t('box.totals.account.settlement.balanceAfter');
});

const balanceAfterMeaning = computed(() => {
  if (isProjected.value) return t('box.totals.account.settlement.balanceAfterProjectedMeaning');
  if ((settlement.value.balanceAfterCents ?? 0) < 0) return t('box.totals.account.settlement.balanceAfterShortMeaning');
  return t('box.totals.account.settlement.balanceAfterMeaning');
});

const cashAtShops = computed(() => (Array.isArray(settlement.value.cashAtShops) ? settlement.value.cashAtShops : []));

function cashLine(cash) {
  const shop = cash?.supplierName || t('box.totals.account.settlement.cashAtShopUnnamed');
  const when = date(cash?.creditedAt);
  return when ? `${shop} · ${when}` : shop;
}

const afterRows = computed(() => (Array.isArray(after.value.rows) ? after.value.rows : []));

function afterLine(row) {
  const title = cartonRowTitle(row?.title, t, te);
  const when = date(row?.at);
  return when ? `${title} · ${when}` : title;
}

// Gains: the backend's `gains` block, «projected» until the receipt is confirmed.
const gains = computed(() => props.account?.gains ?? {});
const poolShare = computed(() => ({
  totalCents: gains.value.poolShare?.totalCents ?? 0,
  balanceCents: gains.value.poolShare?.balanceCents ?? null,
  programs: Array.isArray(gains.value.poolShare?.programs) ? gains.value.poolShare.programs : [],
}));
const poolCredit = computed(() => ({
  totalCents: gains.value.poolCredit?.totalCents ?? 0,
  rows: Array.isArray(gains.value.poolCredit?.rows) ? gains.value.poolCredit.rows : [],
}));
const gainsProjected = computed(() => gains.value.state !== 'realized');
const showGains = computed(() => poolShare.value.totalCents > 0 || poolCredit.value.totalCents > 0);

const gainsHeading = computed(() => t(gainsProjected.value
  ? 'box.totals.account.gains.headingProjected'
  : 'box.totals.account.gains.headingRealized'));

const gainsStateLine = computed(() => {
  if (gainsProjected.value) return t('box.totals.account.gains.stateProjected');
  const on = date(header.value.deliveredConfirmedAt);
  return on
    ? t('box.totals.account.gains.stateRealized', { date: on })
    : t('box.totals.account.gains.stateRealizedUndated');
});

const poolShareLabel = computed(() => t(gainsProjected.value
  ? 'box.totals.account.gains.poolShareProjected'
  : 'box.totals.account.gains.poolShareRealized'));
const poolCreditLabel = computed(() => t(gainsProjected.value
  ? 'box.totals.account.gains.poolCreditProjected'
  : 'box.totals.account.gains.poolCreditRealized'));

// «الكسب الدائم 1%» — the program's Arabic name and rate; a gift has no rate; a name
// with no Arabic part is plain «بولشير».
function programLine(program) {
  const name = programArabicName(program?.title) ?? t('box.totals.account.gains.programPlain');
  return program?.ratePct == null
    ? name
    : t('box.totals.account.gains.programRate', { name, rate: program.ratePct });
}

function poolCreditLine(row) {
  const shop = row?.supplierName
    ? t('box.totals.account.gains.poolCreditFrom', { supplier: row.supplierName })
    : t('box.totals.account.gains.poolCreditFromUnnamed');
  const when = date(row?.at);
  return when ? `${shop} · ${when}` : shop;
}

const footerLine = computed(() => {
  const parts = [];
  if (header.value.invoiceDisplayNumber) {
    parts.push(t('box.totals.account.footerInvoice', { number: header.value.invoiceDisplayNumber }));
  }
  const version = (header.value.invoiceVersion ?? 1) > 1 ? date(header.value.invoiceVersionAt) : null;
  if (version) parts.push(t('box.totals.account.footerVersion', { date: version }));
  return parts.join(' · ');
});
</script>

<style scoped>
.carton-account {
    margin: 0;
    border-radius: var(--poolit-radius-lg);
    overflow: hidden;
}

.elevation-4 {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.ca-header {
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--poolit-border-color);
}

.ca-name {
    font-size: 22px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-headline);
    line-height: 1.2;
}

.ca-meta {
    font-size: 13px;
    color: var(--poolit-text-secondary);
    margin-top: 2px;
}

.ca-state-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.ca-state-row .ca-meta {
    margin-top: 0;
}

.ca-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: var(--poolit-radius-full, 999px);
    font-size: 12px;
    font-weight: var(--poolit-font-weight-bold, 700);
}

.ca-chip-paid {
    background: var(--poolit-success-50, #dcfce7);
    color: var(--poolit-success-700, #166534);
}

.ca-chip-due {
    background: var(--poolit-warning-50, #fef3c7);
    color: var(--poolit-warning-700, #92400e);
}

.ca-chip-pending {
    background: var(--poolit-bg-secondary, #eef0f3);
    color: var(--poolit-text-secondary);
}

.ca-block {
    padding: 12px 16px;
    border-bottom: 1px solid var(--poolit-border-color);
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ca-block-heading {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-primary-600);
    letter-spacing: 0.4px;
    margin-bottom: 2px;
}

.ca-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    font-size: 14px;
    color: var(--poolit-text-primary);
}

.ca-row-sub {
    font-size: 13px;
    color: var(--poolit-text-secondary);
}

.ca-row-indent {
    padding-inline-start: 12px;
}

.ca-row-total {
    font-weight: var(--poolit-font-weight-bold);
    border-top: 1px solid var(--poolit-border-color);
    padding-top: 8px;
    margin-top: 2px;
}

.ca-row-due {
    font-weight: var(--poolit-font-weight-semibold);
}

.ca-row-tap {
    width: 100%;
    border: 0;
    background: transparent;
    font-family: inherit;
    text-align: start;
    cursor: pointer;
    padding: 2px 0;
}

.ca-row-tap.ca-row-indent {
    padding-inline-start: 12px;
}

.ca-row-tap .ca-num {
    text-decoration: underline dotted;
    text-underline-offset: 3px;
}

.ca-pdf {
    padding: 12px 16px 4px;
}

.ca-pdf-cta {
    width: 100%;
}

.ca-pdf-icon {
    margin-inline-end: 8px;
}

.ca-label-wrap {
    min-width: 0;
    overflow-wrap: anywhere;
}

.ca-num {
    flex-shrink: 0;
    font-weight: var(--poolit-font-weight-semibold);
    font-feature-settings: 'tnum' on, 'lnum' on;
    white-space: nowrap;
}

.ca-big {
    font-size: 20px;
    color: var(--poolit-primary-600);
}

.ca-pos {
    color: var(--poolit-success-600, #1b8a3b);
}

.ca-neg {
    color: var(--poolit-danger-600, #b91c1c);
}

.ca-who {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
}

.ca-who-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.ca-who-label {
    font-size: 11px;
    color: var(--poolit-text-secondary);
}

.ca-who-value {
    font-size: 13px;
    overflow-wrap: anywhere;
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.ca-ltr {
    direction: ltr;
    unicode-bidi: isolate;
    text-align: start;
}

.ca-hero {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 4px 0;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1.5px solid var(--poolit-primary-200, #c7d2fe);
    background: var(--poolit-primary-50, #f0f4ff);
}

.ca-hero-neg {
    border-color: var(--poolit-danger-200, #fecaca);
    background: var(--poolit-danger-50, #fef2f2);
}

.ca-hero-label {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-primary-600);
}

.ca-hero-value {
    font-size: 22px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-primary-600);
    font-feature-settings: 'tnum' on, 'lnum' on;
    unicode-bidi: plaintext;
}

.ca-hero-neg .ca-hero-label,
.ca-hero-neg .ca-hero-value {
    color: var(--poolit-danger-600, #b91c1c);
}

.ca-hero-meaning {
    font-size: 11px;
    line-height: 1.5;
    color: var(--poolit-text-secondary);
}

.ca-ask {
    background: var(--poolit-warning-50, #fef3c7);
    color: var(--poolit-warning-700, #92400e);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    font-weight: var(--poolit-font-weight-semibold);
    line-height: 1.5;
}

.ca-ask-cta {
    display: inline-flex;
    margin-top: 8px;
}

.ca-block-gains {
    background: var(--poolit-primary-50, #f5f0ff);
}

.ca-gains-state {
    margin-top: 0;
}

.ca-gift {
    display: inline-block;
    margin-inline-start: 6px;
    padding: 0 6px;
    border-radius: 4px;
    font-size: 10px;
    background: var(--poolit-warning-50, #fef3c7);
    color: var(--poolit-warning-700, #92400e);
}

.ca-footer {
    padding: 10px 16px;
    font-size: 11px;
    color: var(--poolit-text-tertiary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}
</style>
